import { SupportedLocale, UserLocalePreferenceSource } from 'generated/graphql';

const LOCALE_KEY = 'preferredLocale';
const SOURCE_KEY = 'preferredLocaleSource';

/**
 * Persists the user's locale preference (source + locale) so the Apollo link
 * can decide whether to send an Accept-Language override (CR-219).
 */
export const persistLocalePreference = (
  source: UserLocalePreferenceSource,
  locale: SupportedLocale
): void => {
  localStorage.setItem(SOURCE_KEY, source);
  localStorage.setItem(LOCALE_KEY, locale);
};

/**
 * Returns the candidate's stored preferred locale (BCP 47), regardless of
 * source, for passing to mutations like startTest/startPracticeTest (CR-221).
 * The value is persisted on app load by LocalizationWrapper.
 */
export const getStoredPreferredLocale = (): SupportedLocale | undefined => {
  const locale = localStorage.getItem(LOCALE_KEY);
  return (locale as SupportedLocale) ?? undefined;
};

/**
 * Returns the BCP 47 locale to send as an Accept-Language override, or null to
 * send no override (CR-219).
 *
 * We override ONLY when the locale was system-assigned (source === ASSIGNED):
 * an explicitly *selected* locale is already persisted server-side and is
 * authoritative, so no header is needed; an *assigned* locale is only a default
 * that the frontend pins via the header. The override is sent only if it is a
 * locale the backend can actually serve (a member of SupportedLocale).
 */
export const getAssignedAcceptLanguage = (): string | null => {
  const source = localStorage.getItem(SOURCE_KEY);
  const locale = localStorage.getItem(LOCALE_KEY);
  if (source !== UserLocalePreferenceSource.Assigned || !locale) {
    return null;
  }
  return (Object.values(SupportedLocale) as string[]).includes(locale)
    ? locale
    : null;
};
